// 담당: 현정
// Creators 워크스페이스 첫 화면 — 크리에이터 목록(/creators).
//   UI 문법 = for Labels & Creator Solutions의 CreatorGridPage/CreatorCell(App.tsx) 정답지.
//   포털 셸(mx-auto max-w-1320 px-s-6 bg-surface-base) 안의 일반 페이지로 렌더 —
//   워크스페이스식 배경(muted)·헤더 스트립·강제 1320 컨테이너 없음.
//   기능: 검색(300ms 디바운스)·정렬 3종·상세 필터 11축(URL 반영·nullExcluded 고지)·커서 인피니트.
//   실 API만(실데이터 철칙). 카드 클릭 → /creators/:id 워크스페이스.
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Info, SlidersHorizontal, X } from 'lucide-react'
import {
  ApiError,
  fetchCreatorList,
  fetchCreatorListFilters,
  type CreatorFilterOption,
  type CreatorListFilters,
  type CreatorListItem,
  type CreatorListParams,
  type CreatorSort,
} from './creatorsApi'

import { formatIsoDate } from './sections/overview/format'
import { InfoTip } from './sections/InfoTip'

const SORTS: [CreatorSort, string][] = [
  ['subs', '구독자순'],
  ['name', '가나다'],
  ['recent', '최근 업로드'],
]

// 상세 필터 축 — filter-options 키 ↔ URL/API param 키(동일) ↔ 표시 라벨.
const AXES: { key: keyof CreatorListFilters; label: string }[] = [
  { key: 'labels', label: '레이블' },
  { key: 'statuses', label: '계약상태' },
  { key: 'scales', label: '채널 규모' },
  { key: 'genres', label: '콘텐츠 장르' },
  { key: 'languages', label: '구사 언어' },
  { key: 'personalColors', label: '퍼스널 컬러' },
  { key: 'skinTypes', label: '피부 타입' },
  { key: 'skinWorries', label: '피부 고민' },
  { key: 'preferenceIngredients', label: '선호 성분' },
  { key: 'dislikeIngredients', label: '비선호 성분' },
  { key: 'businessTypes', label: '비즈니스 타입' },
]
/** CreatorCell 문법 — 구독자 K 표기(1,440,000 → "1440K"). */
function formatSubsK(n: number): string {
  const v = Math.round(n || 0)
  if (v >= 1000) return `${Math.round(v / 1000).toLocaleString('en-US')}K`
  return String(v)
}

function daysAgo(dateStr: string | null): string | null {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return null
  const diff = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (diff < 0) return null
  return diff === 0 ? '오늘 업로드' : `${diff}일 전 업로드`
}

const GRID_CLS = 'mt-s-6 grid grid-cols-2 gap-s-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
const EMPTY_BOX =
  'mt-s-7 flex min-h-[260px] flex-col items-center justify-center border border-dashed border-line-soft bg-surface-soft p-s-9 text-center'

export default function CreatorListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // ── URL → 조회 파라미터 ──
  const arr = useCallback(
    (key: string) => searchParams.get(key)?.split(',').filter(Boolean) ?? [],
    [searchParams],
  )
  const urlQ = searchParams.get('q') ?? ''
  const sort = (searchParams.get('sort') as CreatorSort) || 'subs'
  const listParams: CreatorListParams = useMemo(
    () => ({
      q: urlQ,
      sort,
      statuses: arr('statuses'),
      scales: arr('scales'),
      labels: arr('labels'),
      genres: arr('genres'),
      languages: arr('languages'),
      personalColors: arr('personalColors'),
      skinTypes: arr('skinTypes'),
      skinWorries: arr('skinWorries'),
      preferenceIngredients: arr('preferenceIngredients'),
      dislikeIngredients: arr('dislikeIngredients'),
      businessTypes: arr('businessTypes'),
    }),
    [urlQ, sort, arr],
  )
  const queryKey = searchParams.toString()

  // ── 로컬 UI ──
  const [qInput, setQInput] = useState(urlQ)
  const [detailOpen, setDetailOpen] = useState(false)
  const [filters, setFilters] = useState<CreatorListFilters | null>(null)

  // ── 데이터 ──
  const [items, setItems] = useState<CreatorListItem[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [nullExcluded, setNullExcluded] = useState<Record<string, number>>({})
  const [status, setStatus] = useState<'loading' | 'error' | 'ok'>('loading')
  const [errorInfo, setErrorInfo] = useState<{
    message: string
    status?: number
    transport?: boolean
  } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const reqIdRef = useRef(0)

  useEffect(() => {
    let alive = true
    fetchCreatorListFilters()
      .then((f) => alive && setFilters(f))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  // 목록 진입 시 스크롤 상단으로(라우트 전환 시 이전 페이지 스크롤 위치 유지 방지).
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // q 디바운스 300ms → URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (qInput.trim() === urlQ.trim()) return
      const next = new URLSearchParams(searchParams)
      if (qInput.trim()) next.set('q', qInput.trim())
      else next.delete('q')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => clearTimeout(t)
  }, [qInput, urlQ, searchParams, setSearchParams])
  useEffect(() => setQInput(urlQ), [urlQ])

  // 필터/정렬/검색 변경 → 1페이지 재조회(race 가드)
  useEffect(() => {
    const reqId = ++reqIdRef.current
    setStatus('loading')
    setItems([])
    setCursor(null)
    fetchCreatorList({ ...listParams, cursor: undefined })
      .then((res) => {
        if (reqId !== reqIdRef.current) return
        setItems(res.items)
        setCursor(res.nextCursor)
        setTotal(res.total)
        setNullExcluded(res.nullExcluded ?? {})
        setStatus('ok')
      })
      .catch((e: unknown) => {
        if (reqId !== reqIdRef.current) return
        const err = e as ApiError
        setErrorInfo({
          message: err?.message ?? '불러오지 못했습니다.',
          status: err?.status,
          transport: err?.transport,
        })
        setStatus('error')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey])

  const loadMore = useCallback(() => {
    if (loadingMore || !cursor || status !== 'ok') return
    const reqId = reqIdRef.current
    setLoadingMore(true)
    fetchCreatorList({ ...listParams, cursor })
      .then((res) => {
        if (reqId !== reqIdRef.current) return
        setItems((prev) => [...prev, ...res.items])
        setCursor(res.nextCursor)
      })
      .catch(() => {})
      .finally(() => {
        if (reqId === reqIdRef.current) setLoadingMore(false)
      })
  }, [loadingMore, cursor, status, listParams])

  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { rootMargin: '600px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [loadMore])

  // ── 필터 조작 ──
  function toggleValue(param: string, value: string) {
    const cur = arr(param)
    const next = new URLSearchParams(searchParams)
    const updated = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
    if (updated.length) next.set(param, updated.join(','))
    else next.delete(param)
    setSearchParams(next, { replace: true })
  }
  function clearAll() {
    const next = new URLSearchParams()
    if (sort !== 'subs') next.set('sort', sort)
    if (urlQ.trim()) next.set('q', urlQ.trim())
    setSearchParams(next, { replace: true })
  }
  function setSort(s: CreatorSort) {
    const next = new URLSearchParams(searchParams)
    if (s === 'subs') next.delete('sort')
    else next.set('sort', s)
    setSearchParams(next, { replace: true })
  }

  const optionLabel = useCallback(
    (axis: keyof CreatorListFilters, value: string): string => {
      if (value === '__none__') return axis === 'labels' ? '레이블 미지정' : '미지정'
      return filters?.[axis]?.find((o) => o.value === value)?.label ?? value
    },
    [filters],
  )

  const appliedChips = AXES.flatMap((axis) =>
    arr(axis.key).map((value) => ({ axis: axis.key, axisLabel: axis.label, value })),
  )
  const nullExcludedTotal = Object.values(nullExcluded).reduce((a, b) => a + (b || 0), 0)

  return (
    <div className="pt-s-7">
      {/* 페이지 헤더 — eyebrow(BIC) + 타이틀 + Apps 홈 버튼 (CreatorGridPage 문법) */}
      <div className="flex flex-wrap items-center justify-between gap-s-4">
        <div>
          <p className="lf-eyebrow text-fg-3">▪ Brand Intelligence Consulting</p>
          <h1 className="mt-s-2 lf-h3 text-fg-1" style={{ fontFamily: 'var(--font-display)' }}>
            크리에이터 목록
          </h1>
        </div>
        <div className="flex items-center gap-s-3">
          <Link
            to="/creators/brand-links"
            className="inline-flex h-[34px] items-center border border-line-soft bg-paper px-3 font-text text-[13px] font-medium text-fg-1"
          >
            Shared documents
          </Link>
          <button
            type="button"
            onClick={() => navigate('/?tab=bic')}
            className="border border-line-soft bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 hover:border-line-strong hover:text-fg-1"
          >
            ← Apps 홈
          </button>
        </div>
      </div>

      {/* 섹션 헤더 — Channels 문법(border-b border-fg-1) */}
      <div className="mt-s-9 flex items-baseline gap-s-3 border-b border-fg-1 pb-s-3">
        <span className="lf-eyebrow text-fg-1" style={{ letterSpacing: '0.16em' }}>
          Creators
        </span>
        {status === 'ok' && (
          <span className="inline-flex items-center gap-1.5 lf-body-sm tabular-nums text-fg-3">
            크리에이터 {total.toLocaleString('ko-KR')}명
            {/* leferi_creator 가 켜지는 경로는 **둘**이라 둘 다 적는다(로컬 실측 2026-08-18).
                  · SalesforceCreatorSyncConfiguration — Creator_ContractType__c 가 있으면 1
                  · InstagramCreatorSocialBackfillConfiguration — 닉네임 매칭 실패 시 새 크리에이터를
                    만들며 계약과 무관하게 1 (leferi_creator=1 & enabled=1 중 47명이 계약 형태 없음)
                세일즈포스만 적으면 그 47명이 설명되지 않는다.

                제외 조건도 둘이다. 해지는 계약이 입력된 상태라 안 적으면 포함되는 것으로 읽히고,
                채널 없음은 SUBQUERY_SOCIAL 이 INNER JOIN 이라 조건을 다 통과해도 목록에서 빠진다
                (해지 제외 후 981명 중 17명). 안 적으면 "계약했는데 왜 없지"가 된다. */}
            <InfoTip
              label="크리에이터 목록 노출 기준"
              text="세일즈포스 계약 등록 또는 채널 수집 과정에서 등록된 크리에이터입니다. 계약 해지·채널 없음은 제외됩니다."
            />
          </span>
        )}
      </div>

      {/* 툴바 — 검색 · 결과 수 · 상세 필터 토글 · 정렬 */}
      <div className="mt-s-6 flex flex-wrap items-center justify-between gap-s-3">
        <div className="flex flex-wrap items-center gap-s-3">
          <div className="relative w-full max-w-[320px]">
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="채널명으로 검색"
              aria-label="이름·채널명·핸들 검색"
              className="w-full border border-line-soft bg-surface-base py-s-2 pl-s-8 pr-s-3 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:shadow-focus focus-visible:outline-none"
            />
            <span
              className="pointer-events-none absolute left-s-3 top-1/2 -translate-y-1/2 text-fg-3"
              aria-hidden="true"
            >
              ⌕
            </span>
          </div>
          {status === 'ok' && (
            <span className="lf-body-sm tabular-nums text-fg-3">
              {total.toLocaleString('ko-KR')}명
            </span>
          )}
          <button
            type="button"
            onClick={() => setDetailOpen((v) => !v)}
            aria-expanded={detailOpen}
            className={[
              'inline-flex items-center gap-s-2 border px-s-3 py-s-2 lf-body-sm',
              detailOpen || appliedChips.length > 0
                ? 'border-brand-blue text-brand-blue'
                : 'border-line-soft text-fg-2 hover:text-fg-1',
            ].join(' ')}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            상세 필터
            {appliedChips.length > 0 && (
              <span className="tabular-nums"> · {appliedChips.length}</span>
            )}
          </button>
        </div>
        <div className="inline-flex border border-line-soft" role="group" aria-label="정렬">
          {SORTS.map(([key, lbl], i) => (
            <button
              key={key}
              type="button"
              onClick={() => setSort(key)}
              aria-pressed={sort === key}
              className={[
                'px-s-3 py-s-2 lf-body-sm font-medium transition-colors duration-base ease-standard',
                i > 0 ? 'border-l border-line-soft' : '',
                sort === key
                  ? 'bg-brand-blue text-fg-inverse'
                  : 'bg-surface-base text-fg-2 hover:text-fg-1',
              ].join(' ')}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* 적용된 필터 칩 + 미입력 고지 */}
      {(appliedChips.length > 0 || (status === 'ok' && nullExcludedTotal > 0)) && (
        <div className="mt-s-3 flex flex-wrap items-center gap-s-2">
          {appliedChips.length > 0 && (
            <>
              <span className="lf-body-sm text-fg-3">적용된 필터</span>
              {appliedChips.map((c) => (
                <span
                  key={`${c.axis}-${c.value}`}
                  className="inline-flex items-center gap-s-2 border border-line-soft py-[3px] pl-s-3 pr-s-2 text-[12px] text-fg-2"
                >
                  {c.axisLabel} {optionLabel(c.axis, c.value)}
                  <button
                    type="button"
                    onClick={() => toggleValue(c.axis, c.value)}
                    aria-label={`${c.axisLabel} ${optionLabel(c.axis, c.value)} 해제`}
                    className="inline-flex text-fg-3 hover:text-fg-1"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="px-s-1 text-[12px] text-brand-blue"
              >
                전체 해제
              </button>
            </>
          )}
          {status === 'ok' && nullExcludedTotal > 0 && (
            <span className="ml-auto inline-flex items-center gap-s-2 lf-body-sm text-fg-3">
              <Info className="h-3.5 w-3.5" aria-hidden />
              속성 미입력{' '}
              <span className="tabular-nums">{nullExcludedTotal.toLocaleString('ko-KR')}</span>명
              제외됨
            </span>
          )}
        </div>
      )}

      {/* 상세 필터 패널(칩 클라우드) — 흰 배경 문법, 접힘 기본 */}
      {detailOpen && (
        <div className="mt-s-3 border border-line-soft bg-surface-base p-s-5">
          {/* 우상단 닫기 — 토글 버튼과 별개로 패널에서 직접 닫기 */}
          <div className="mb-s-2 flex justify-end">
            <button
              type="button"
              onClick={() => setDetailOpen(false)}
              aria-label="상세 필터 닫기"
              className="inline-flex items-center gap-s-1 border border-line-soft px-s-2 py-s-1 lf-body-sm text-fg-2 hover:border-line-strong hover:text-fg-1"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              닫기
            </button>
          </div>
          {AXES.map((axis) => {
            const options = filters?.[axis.key] ?? []
            if (options.length === 0) return null
            const selected = arr(axis.key)
            return (
              <div key={axis.key} className="flex items-start gap-s-4 py-s-2">
                <div className="flex-none basis-[104px] pt-[6px] lf-body-sm text-fg-3">
                  {axis.label}
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap gap-s-2">
                  {options.map((o: CreatorFilterOption) => {
                    const on = selected.includes(o.value)
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => toggleValue(axis.key, o.value)}
                        aria-pressed={on}
                        className={[
                          'whitespace-nowrap border px-s-3 py-[5px] text-[12px]',
                          on
                            ? 'border-brand-blue bg-brand-blue font-semibold text-fg-inverse'
                            : 'border-line-soft bg-surface-base text-fg-2 hover:border-line-strong hover:text-fg-1',
                        ].join(' ')}
                      >
                        {o.label}
                        {o.count != null && (
                          <span className={on ? 'ml-s-1 text-fg-inverse/70' : 'ml-s-1 text-fg-3'}>
                            {o.count}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
          {!filters && <div className="py-s-2 lf-body-sm text-fg-3">필터 옵션 불러오는 중…</div>}
        </div>
      )}

      {/* 결과 */}
      <ListBody
        status={status}
        errorInfo={errorInfo}
        items={items}
        q={urlQ}
        total={total}
        cursor={cursor}
        loadingMore={loadingMore}
        sentinelRef={sentinelRef}
      />
    </div>
  )
}

function ListBody({
  status,
  errorInfo,
  items,
  q,
  total,
  cursor,
  loadingMore,
  sentinelRef,
}: {
  status: 'loading' | 'error' | 'ok'
  errorInfo: { message: string; status?: number; transport?: boolean } | null
  items: CreatorListItem[]
  q: string
  total: number
  cursor: string | null
  loadingMore: boolean
  sentinelRef: RefObject<HTMLDivElement>
}) {
  if (status === 'error') {
    const isAuth = errorInfo?.status === 401
    return (
      <div className={EMPTY_BOX}>
        <p className="lf-eyebrow text-status-danger">▪ {isAuth ? 'No access' : 'Error'}</p>
        <p className="mt-s-3 lf-h3 text-fg-1">
          {isAuth ? '로그인이 필요합니다.' : '목록을 불러오지 못했어요.'}
        </p>
        {!isAuth && errorInfo?.message && (
          <p className="mt-s-2 lf-body-sm text-fg-3">
            {errorInfo.message}
            {errorInfo.transport && errorInfo.status ? ` (${errorInfo.status})` : ''}
          </p>
        )}
      </div>
    )
  }

  if (status === 'loading' && items.length === 0) {
    return (
      <div className={GRID_CLS}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="animate-pulse border border-line-soft bg-surface-soft"
            style={{ aspectRatio: '3 / 4' }}
          />
        ))}
      </div>
    )
  }

  if (status === 'ok' && items.length === 0) {
    return (
      <div className={EMPTY_BOX}>
        <p className="lf-eyebrow text-fg-3">▪ Creators</p>
        <p className="mt-s-3 lf-h3 text-fg-1">검색 결과가 없어요.</p>
        <p className="mt-s-2 lf-body-sm text-fg-3">
          {q.trim()
            ? `“${q.trim()}”와 일치하는 이름 · 채널명 · 핸들이 없습니다.`
            : '조건에 맞는 크리에이터가 없습니다.'}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className={GRID_CLS}>
        {items.map((c) => (
          <CreatorCard key={c.id} creator={c} />
        ))}
      </div>
      <div ref={sentinelRef} />
      {cursor ? (
        <div className="mt-s-6 flex items-center justify-center gap-s-3 py-s-7">
          {loadingMore && (
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-line-soft border-t-brand-blue"
              aria-hidden
            />
          )}
          <span className="lf-body-sm text-fg-3">
            {loadingMore ? '20명 추가 로드 중…' : '스크롤하면 더 불러옵니다'}
          </span>
        </div>
      ) : (
        <div className="mt-s-6 py-s-7 text-center lf-body-sm text-fg-3">
          — 크리에이터 {total.toLocaleString('ko-KR')}명 전부 표시 —
        </div>
      )}
    </>
  )
}

// 카드 — CreatorCell(App.tsx) 문법 재사용/확장. 3:4 풀블리드 + 스크림 + 하단 오버레이.
// 우리 추가: 우상 배지(규모/레이블/수집 지연) · 하단 메타(계약상태·N일 전).
//   수집 지연 마커는 **중단 의심(D+14 이상)만** 띄운다 — D+3~13까지 표시하면 목록 전체가 마커로
//   덮여 신호가 죽는다. 업로드 신선도(하단 메타)와 수집 신선도는 다른 개념이라 자리도 분리한다.
function CreatorCard({ creator: c }: { creator: CreatorListItem }) {
  const upload = daysAgo(c.lastUploadAt)
  const showScale = c.scaleLabel === '메가' || c.scaleLabel === '탑'
  const meta = [c.contractType, upload].filter(Boolean).join(' · ')
  // 수집 기준일은 title 로만 남긴다 — 카드 위 "수집 N일 경과" 배지는 제거했다(2026-08-18).
  const collectedAt = formatIsoDate(c.lastCollectedAt)
  const cardTitle = collectedAt ? `${c.name} · 채널 지표 수집 ${collectedAt}` : c.name
  return (
    <Link
      to={`/creators/${encodeURIComponent(c.id)}`}
      className="group relative block w-full overflow-hidden border border-line-soft transition-colors duration-base ease-standard hover:border-line-strong focus-visible:shadow-focus focus-visible:outline-none"
      style={{ aspectRatio: '3 / 4' }}
      title={cardTitle}
    >
      <div className="absolute inset-0 overflow-hidden bg-grad-blue-brown">
        {c.photoUrl && (
          <img
            src={c.photoUrl}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-slow ease-out-soft group-hover:scale-[1.04]"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(26,18,19,0) 36%, rgba(26,18,19,0.5) 64%, rgba(26,18,19,0.9) 100%)',
          }}
        />
        {/* 좌상 eyebrow */}
        <div className="absolute left-s-4 top-s-4 flex items-center gap-s-2">
          <span className="h-[7px] w-[7px]" style={{ background: 'var(--lcmd-pink, #FC8FFF)' }} />
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80"
            style={{ fontFamily: 'var(--font-text)' }}
          >
            Creator
          </span>
        </div>
        {/* 우상 배지 — 규모 / 레이블 */}
        <div className="absolute right-s-3 top-s-3 flex flex-col items-end gap-[5px]">
          {showScale && (
            <span className="bg-black/55 px-s-2 py-[2px] text-[11px] font-semibold tracking-[0.1em] text-white">
              {c.scaleLabel}
            </span>
          )}
          {c.label && (
            <span className="bg-black/45 px-s-2 py-[2px] text-[11px] tracking-[0.04em] text-white/90">
              {c.label}
            </span>
          )}
        </div>
        {/* 하단 오버레이 */}
        <div className="absolute inset-x-0 bottom-0 p-s-4">
          <p
            className="line-clamp-2 break-keep font-bold text-white"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '16px',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            {c.name}
          </p>
          <p className="mt-[6px] tabular-nums text-white/95" style={{ fontSize: '16px' }}>
            <span className="font-light" style={{ fontFamily: 'var(--font-text)' }}>
              {formatSubsK(c.subscribers)}
            </span>
            <span
              className="ml-[5px] font-light text-white/50"
              style={{ fontSize: '11px', letterSpacing: '0.04em' }}
            >
              Subscribers
            </span>
          </p>
          {meta && (
            <p
              className="mt-[3px] font-light text-white/60"
              style={{ fontFamily: 'var(--font-text)', fontSize: '11px', letterSpacing: '0.02em' }}
            >
              {meta}
            </p>
          )}
        </div>
      </div>
    </Link>
  )
}
